import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { getMonthInfo } from './helpers';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import MonthCalendar from './MonthCalendar';
import { CalendarContent, CalendarWrapper } from './shared.styles';
import { TSwitcherPeriod } from './PeriodSwitcher';

interface IDateRangeDialogProps {
  minDate: moment.Moment;
  maxDate: moment.Moment;
  fromDate: moment.Moment | undefined;
  toDate: moment.Moment | undefined;
  startWeekDay: string;
  isVisible: boolean;
  datePeriod?: TSwitcherPeriod;
  needToScrollToEnd: boolean;
  setNeedToScroll: (flag: boolean) => void;
}

const DateRangeDialog: React.FC<IDateRangeDialogProps> = (props) => {
  const {
    minDate,
    maxDate,
    fromDate,
    toDate,
    startWeekDay,
    isVisible,
    datePeriod,
    needToScrollToEnd,
    setNeedToScroll,
  } = props;

  const [rowCount, setRowCount] = useState(2400);
  const minYear = minDate ? moment(minDate).year() : 1900;
  const minMonth = minDate ? moment(minDate).month() : 0;
  const listRef = useRef<any>();

  useEffect(() => {
    if (maxDate) {
      const _minDate = minDate ? minDate : moment('2010-01-01', 'YYYY-MM-DD');
      setRowCount(
        moment(maxDate).diff(_minDate, 'month') +
          (datePeriod === 'week' || maxDate.date() < _minDate.date() ? 2 : 1)
      );
    }
  }, [maxDate, minDate, datePeriod]);

  useEffect(() => {
    if (listRef.current && isVisible) {
      const date = toDate ? moment(toDate) : moment();
      const minDateValue = moment(minDate);
      let monthDiff = date.diff(moment('1900-01-01', 'YYYY-MM-DD'), 'month');

      if (minDateValue.isValid()) {
        monthDiff = date.diff(minDateValue, 'month');
      }

      listRef.current.scrollToItem(
        monthDiff + (datePeriod === 'week' ? 1 : 0),
        'smart'
      );
    }
    if (needToScrollToEnd) setNeedToScroll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, needToScrollToEnd, datePeriod]);

  function getMonthYearFromIndex(index: number): {
    year: number;
    month: number;
  } {
    const _index = index + minMonth;
    const year = minYear + Math.floor(_index / 12);
    const month = _index % 12;

    return { year, month };
  }

  const Row = ({
    index,
    style,
  }: {
    index: number;
    style: any;
  }): JSX.Element => {
    const { year, month } = getMonthYearFromIndex(index);

    return (
      <div style={style}>
        <MonthCalendar
          month={month}
          year={year}
          fromDate={fromDate}
          toDate={toDate}
          startWeekDay={startWeekDay}
          minDate={minDate}
          maxDate={maxDate}
        />
      </div>
    );
  };

  function getItemSize(index: number): number {
    const { year, month } = getMonthYearFromIndex(index);
    const { totalWeek } = getMonthInfo(year, month, 'sunday');

    return totalWeek.length * 40 + 45;
  }

  function renderMonthCalendars(): JSX.Element {
    return (
      <AutoSizer>
        {({
          height,
          width,
        }: {
          height: number;
          width: number;
        }): JSX.Element => {
          return (
            <List
              ref={listRef}
              width={width}
              height={height}
              itemCount={rowCount}
              itemSize={getItemSize}
            >
              {Row}
            </List>
          );
        }}
      </AutoSizer>
    );
  }

  return (
    <CalendarWrapper>
      <CalendarContent>{renderMonthCalendars()}</CalendarContent>
    </CalendarWrapper>
  );
};

export default DateRangeDialog;
